
import { useState, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useDebounce } from '@/hooks/useDebounce';
import { ChatListItem } from '@/types/chat';
import { debugLogger } from '@/utils/debugLogger';

export interface SearchResult {
  conversationId: string;
  partnerId: string;
  partnerUsername: string;
  partnerAvatar: string;
  matchedMessage?: string;
  matchType: 'username' | 'message';
  lastMessageTime: string | null;
  messageCreatedAt?: string | null; // Horário específico da mensagem encontrada
  messageId?: string; // ID único da mensagem para resultados múltiplos
  unreadCount: number;
}

interface UseOptimizedChatSearchProps {
  conversations: ChatListItem[];
  profiles: Array<{
    id: string;
    username: string;
    main_photo_url: string;
    hidden: boolean;
    onboarding_completed: boolean;
  }>;
}

export function useOptimizedChatSearch({ conversations, profiles }: UseOptimizedChatSearchProps) {
  const { user } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const queryClient = useQueryClient();

  // Create profiles map for quick lookup
  const profilesMap = useMemo(() => {
    return new Map(profiles.map(p => [p.id, p]));
  }, [profiles]);

  // Search by username locally (no API call needed)
  const usernameResults = useMemo(() => {
    if (!debouncedSearchTerm.trim()) return [];

    const results: SearchResult[] = [];
    
    for (const conv of conversations) {
      const partner = conv.partner;
      if (!partner?.username) continue;

      if (partner.username.toLowerCase().includes(debouncedSearchTerm.toLowerCase())) {
        results.push({
          conversationId: conv.id,
          partnerId: partner.id,
          partnerUsername: partner.username,
          partnerAvatar: partner.avatar || '',
          matchType: 'username',
          lastMessageTime: conv.time,
          unreadCount: conv.unread
        });
      }
    }

    return results;
  }, [conversations, debouncedSearchTerm]);

  // Search by message content (only API call when needed)
  const { 
    data: messageResults = [], 
    isLoading: messageSearchLoading 
  } = useQuery({
    queryKey: ['message-search', debouncedSearchTerm, user?.id],
    queryFn: async () => {
      if (!user?.id || !debouncedSearchTerm.trim()) return [];

      try {
        // Get conversation IDs that user participates in and are not deleted
        const conversationIds = conversations.map(c => c.id);
        if (!conversationIds.length) return [];

        // Search messages only in user's non-deleted conversations
        const { data: messageMatches, error } = await supabase
          .from('messages')
          .select(`
            id,
            conversation_id, 
            content, 
            created_at,
            conversations!inner(
              id,
              deleted_at
            )
          `)
          .ilike('content', `%${debouncedSearchTerm}%`)
          .eq('content_type', 'text')
          .in('conversation_id', conversationIds)
          .is('conversations.deleted_at', null) // FILTRO ADICIONADO: Excluir mensagens de conversas deletadas
          .order('created_at', { ascending: false })
          .limit(100);

        if (error) {
          debugLogger.error('Message search error:', error);
          return [];
        }

        if (!messageMatches?.length) return [];

        const results: SearchResult[] = [];
        
        for (const message of messageMatches) {
          const conv = conversations.find(c => c.id === message.conversation_id);
          if (!conv) continue;

          // Cada mensagem gera um resultado separado (não consolida por conversa)
          results.push({
            conversationId: conv.id,
            partnerId: conv.partner.id,
            partnerUsername: conv.partner.username || 'Usuário',
            partnerAvatar: conv.partner.avatar || '',
            matchedMessage: message.content,
            matchType: 'message',
            lastMessageTime: conv.time,
            messageCreatedAt: message.created_at, // Usar horário específico da mensagem
            messageId: message.id, // ID único para diferenciar resultados
            unreadCount: conv.unread
          });
        }

        return results;
      } catch (error) {
        debugLogger.error('[useOptimizedChatSearch] Message search error:', error);
        return [];
      }
    },
    enabled: !!user?.id && debouncedSearchTerm.trim().length > 0,
    staleTime: 10000,
    gcTime: 60000,
  });

  // Combine username and message results
  const searchResults = useMemo(() => {
    if (!debouncedSearchTerm.trim()) return [];

    const combined = [...usernameResults];
    
    // Adicionar todos os resultados de mensagem (não consolidar)
    for (const msgResult of messageResults) {
      combined.push(msgResult);
    }

    return combined;
  }, [usernameResults, messageResults, debouncedSearchTerm]);

  // Invalidate search cache when conversations change
  const invalidateSearchCache = () => {
    queryClient.invalidateQueries({ queryKey: ['message-search'] });
  };

  return {
    searchTerm,
    setSearchTerm,
    searchResults,
    searchLoading: messageSearchLoading,
    isSearching: debouncedSearchTerm.trim().length > 0,
    invalidateSearchCache
  };
}
